import { Component, Inject, NgZone, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';

export interface UploadProgressData {
  fileName: string;
}

@Component({
  selector: 'app-upload-progress-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatProgressBarModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>Uploading File</h2>
    <mat-dialog-content>
      <p class="file-name">{{ data.fileName }}</p>
      <mat-progress-bar [mode]="progressMode" [value]="progressValue"></mat-progress-bar>
      <div class="stats" *ngIf="progressValue > 0 && !completed && !errorMessage">
        <span>{{ speedText }}</span>
        <span>{{ etaText }}</span>
      </div>
      <p class="status-label">{{ statusLabel }}</p>
      <p *ngIf="errorMessage" class="error">{{ errorMessage }}</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="cancel()" [disabled]="completed">Cancel</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .file-name { font-weight: 500; word-break: break-all; margin-bottom: 12px; }
    .status-label { color: rgba(0,0,0,.6); font-size: 14px; margin-top: 8px; }
    .stats { display: flex; justify-content: space-between; font-size: 13px; color: rgba(0,0,0,.54); margin-top: 6px; }
    .error { color: #f44336; margin-top: 8px; }
    mat-progress-bar { margin-bottom: 4px; }
  `]
})
export class UploadProgressDialogComponent {
  statusLabel = 'Uploading…';
  progressMode: 'indeterminate' | 'determinate' = 'indeterminate';
  progressValue = 0;
  errorMessage = '';
  completed = false;
  speedText = '';
  etaText = '';

  private abortController = new AbortController();
  private uploadStartTime = 0;

  constructor(
    private dialogRef: MatDialogRef<UploadProgressDialogComponent>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: UploadProgressData
  ) {}

  get signal(): AbortSignal {
    return this.abortController.signal;
  }

  startProgress(): void {
    this.uploadStartTime = Date.now();
    this.progressMode = 'determinate';
    this.progressValue = 0;
    this.statusLabel = 'Uploading…';
  }

  setUploadProgress(percent: number, loaded: number, total: number): void {
    this.zone.run(() => {
      this.progressValue = Math.round(percent);

      const elapsed = (Date.now() - this.uploadStartTime) / 1000;
      if (elapsed < 1) {
        this.statusLabel = `Uploading… ${this.progressValue}%`;
        this.cdr.detectChanges();
        return;
      }

      const bytesPerSec = loaded / elapsed;
      this.speedText = this.formatSpeed(bytesPerSec);

      const remaining = total - loaded;
      const etaSec = bytesPerSec > 0 ? remaining / bytesPerSec : 0;
      this.etaText = this.formatEta(etaSec);

      this.statusLabel = `Uploading… ${this.progressValue}%`;
      this.cdr.detectChanges();
    });
  }

  setConfirming(): void {
    this.statusLabel = 'Finalizing…';
    this.progressMode = 'indeterminate';
    this.speedText = '';
    this.etaText = '';
  }

  setCompleted(): void {
    this.completed = true;
    this.statusLabel = 'Upload complete';
    this.progressMode = 'determinate';
    this.progressValue = 100;
    this.speedText = '';
    this.etaText = '';
  }

  setError(message: string): void {
    this.errorMessage = message;
    this.statusLabel = 'Upload failed';
    this.progressMode = 'determinate';
    this.progressValue = 0;
  }

  cancel(): void {
    this.abortController.abort();
    this.dialogRef.close('cancelled');
  }

  private formatSpeed(bytesPerSec: number): string {
    if (bytesPerSec >= 1_048_576) {
      return (bytesPerSec / 1_048_576).toFixed(1) + ' MB/s';
    }
    return (bytesPerSec / 1024).toFixed(0) + ' KB/s';
  }

  private formatEta(seconds: number): string {
    if (seconds < 1) return 'almost done';
    if (seconds < 60) return Math.ceil(seconds) + 's remaining';
    const min = Math.floor(seconds / 60);
    const sec = Math.ceil(seconds % 60);
    return `${min}m ${sec}s remaining`;
  }
}
